/* eslint-disable no-shadow */
/* eslint-disable no-return-assign */
/* eslint-disable no-underscore-dangle */
import useTranslation from 'next-translate/useTranslation'

import {
  SearchJobsQuery,
  SearchJobsDocument,
  Job,
  QuerySearchJobsArgs,
  OrderDirection,
  JobsSearchOrderField,
  JobsSearchOrderBy,
  Company,
  LocaleEnum,
} from 'generated/apolloComponents'
import { useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { BellIcon } from '@heroicons/react/24/solid'
import JobListItemWrapper from '@/components/molecules/JobListItem/JobListItemWrapper'
import { initializeApollo } from '@/api/apollo'
import MultiSelect, { SelectDataType } from '@/components/atoms/Select/Multi'

import { arrayOfObjectToStringArrayByKey, isEmpty } from '@/helpers/utils'
import Pagination from '@/components/molecules/Pagination'
import FiltersList from '@/components/molecules/FiltersList'
import { sorting } from '@/data/sortingOptions'
import {
  ActionType,
  convertJobsFiltersSlugToURL,
  convertJobStateFiltersToQueryFilters,
  JobListContext,
  JobStateFilters,
  OrderStateType,
} from '@/context/jobListContext'
import Button from '../atoms/Button'
import { useMe } from '@/context/meContext'
import JobListIcons from '../molecules/JobListIcons'

interface iProps {
  jobs: Job[]
  jobsLocations: string[]
  totalCount: number
  slugState?: any
  company: Company
  domainLang?: string
}

const CompanyJobList = ({
  jobs,
  jobsLocations,
  totalCount,
  slugState,
  company,
  domainLang,
}: iProps) => {
  const router = useRouter()
  const [jobsList, setJobsList] = useState<Job[]>(jobs)
  const [jobsCount, setJobsCount] = useState(totalCount)
  const isMounted = useRef(false)
  const { state, dispatch } = useContext(JobListContext)
  const { t, lang } = useTranslation()
  const [companyHasOffers, setCompanyHasOffers] = useState<boolean | null>(
    company ? null : true
  )
  const dynamicRoute = useRouter().asPath
  const { me, loadMe } = useMe()

  const queryLanguage = LocaleEnum[(domainLang || lang) as 'pl' | 'en']
  const [minSalaryFilter, setMinSalaryFilter] = useState<number | null>(null)
  const scrollToRef = useRef<HTMLDivElement>(null)

  const pressesGeneratorLink = () => {
    const prefixString = '/admin/shortener/jobs'

    return (
      prefixString +
      convertJobsFiltersSlugToURL(state, true, (e: any) =>
        encodeURIComponent(e)
      )
    )
  }

  useEffect(() => {
    if (router.asPath === '/companies/jobs') {
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { experienceLevel: [], city: [], role: [], skills: [] },
      })
    }
  }, [dynamicRoute])

  useEffect(() => {
    const slug = convertJobsFiltersSlugToURL(state)

    const loadData = async function () {
      const apolloClient = initializeApollo({})
      if (slugState) {
        router.push({
          pathname: `/companies/jobs${slug}`,
        })
      }
      let filters = convertJobStateFiltersToQueryFilters(
        state.filters as JobStateFilters
      )
      const slugFilters = filters

      filters = {
        company: [company.id],
        ...filters,
      }

      const variables: QuerySearchJobsArgs = {
        page: state.page,
        perPage: state.perPage,
        filters: filters,
        language: queryLanguage,
      }
      if (me && me.rejectedJobs) variables.exclude = [...me.rejectedJobs]

      if (company.sorting === 'brackets')
        variables.order = {
          field: 'SHOW_SALARY',
          direction: 'DESC',
        } as JobsSearchOrderBy
      if (
        state.order &&
        (state.order.value as JobsSearchOrderBy).field !== 'DEFAULT'
      )
        variables.order = state.order.value as JobsSearchOrderBy

      const jobsData = await apolloClient.query<
        SearchJobsQuery,
        QuerySearchJobsArgs
      >({
        query: SearchJobsDocument,
        variables: variables,
      })

      let jobsUniqueByKey: any = []
      let jobsByCityArray: any = []
      let concatJobs = []
      concatJobs = [...jobsData.data.searchJobs.nodes!]

      let jobLocationFilters = {}
      if (company && company.locations) {
        const cityKey = lang === 'pl' ? 'cityPl' : 'cityEn'
        const cities = arrayOfObjectToStringArrayByKey(
          company.locations,
          cityKey
        )
        jobLocationFilters = { city: cities, ...slugFilters }
      }
      const variablesTwo: QuerySearchJobsArgs = {
        page: 1,
        perPage: 10,
        filters: jobLocationFilters,
        language: queryLanguage,
      }

      if (me && me.rejectedJobs) variablesTwo.exclude = [...me.rejectedJobs]

      const jobsByCityData = await apolloClient.query<
        SearchJobsQuery,
        QuerySearchJobsArgs
      >({
        query: SearchJobsDocument,
        variables: variablesTwo,
      })

      jobsByCityArray = jobsByCityData.data.searchJobs.nodes
      if (companyHasOffers === null) {
        jobsData.data.searchJobs.nodes!.length < 10
          ? setCompanyHasOffers(false)
          : setCompanyHasOffers(true)
      }
      concatJobs = [...jobsData.data.searchJobs.nodes!]

      jobsUniqueByKey = [
        // @ts-ignore
        ...new Map(concatJobs.map((item) => [item.id, item])).values(),
      ]

      setJobsList(jobsUniqueByKey)
      setJobsCount(jobsData.data.searchJobs.totalCount)
    }
    isMounted.current ? loadData() : (isMounted.current = true)
  }, [state, me])

  useEffect(() => {
    loadMe(true)
  }, [state])

  useEffect(() => {
    if (slugState && !isEmpty(slugState)) {
      dispatch({ type: ActionType.SetState, payload: slugState })
    }
  }, [])

  const handlePageChange = (page: number) => {
    dispatch({ type: ActionType.ChangePage, payload: page })
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleOrderChange = (value: SelectDataType | null, key: string) => {
    if (value) {
      let sorting = null
      if (typeof value.value === 'string') {
        sorting = value.value.split(',')
        sorting = {
          direction: sorting[1] as OrderDirection,
          field: sorting[0] as JobsSearchOrderField,
        }
        const tmp: OrderStateType = {
          label: value.label,
          value: sorting,
        }

        dispatch({ type: ActionType.ChangeOrder, payload: tmp })
      }
    }
  }

  useEffect(() => {
    state.filters?.salaryBrackets &&
      setMinSalaryFilter(state.filters.salaryBrackets.min)
  }, [state])

  return (
    <div ref={scrollToRef} className='bg-gray-bg py-8 relative'>
      <div className='container'>
        <div className='mb-6 md:mb-10'>
          <div className='flex'>
            <div className='mt-2'>
              <div className='md:flex mt-4 md:mt-6'>
                <FiltersList state={state} dispatch={dispatch} />
              </div>
            </div>
            <div className='ml-auto'>
              {!company && (
                <a
                  href='/account'
                  title={t('common:subscribe_offers')}
                  className='bg-purple font-normal text-white flex justify-center items-center md:inline-block text-center  md:py-3 rounded-full mb-4 hover:bg-action-hover h-12 md:h-auto w-12  md:w-full'
                >
                  <BellIcon
                    className='md:mr-3 inline-block'
                    width='13'
                    height='15'
                  />
                  <span className='hidden md:inline-block'>
                    {t('common:subscribe_offers')}
                  </span>
                </a>
              )}

              <MultiSelect
                state={state}
                type='single'
                value={
                  state.order || {
                    label: t('common:default'),
                    value: { direction: 'ASC', field: 'DEFAULT' },
                  }
                }
                name='order'
                onSelect={handleOrderChange}
                data={sorting.map(
                  (item) =>
                    (item = { ...item, label: t(`common:${item.label}`) })
                )}
                title={t('common:sort')}
                className='flex-1 w-96 multiSelect--small hidden md:block'
              />
              {me && me.__typename === 'Admin' ? (
                <Button
                  className='w-full mt-3'
                  href={pressesGeneratorLink()}
                  type='primary'
                >
                  Generuj prasówkę
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        <JobListIcons />
        {jobsList.length > 0 &&
          jobsList.map((job) => (
            <JobListItemWrapper
              key={job.id}
              job={job}
              me={me!}
              minSalaryFilter={minSalaryFilter}
            />
          ))}
      </div>
      <Pagination
        className='pagination-bar'
        currentPage={state.page}
        totalCount={jobsCount}
        pageSize={state.perPage}
        onPageChange={(page: number) => handlePageChange(page)}
      />
    </div>
  )
}

export default CompanyJobList
